import useSWR, { SWRConfiguration } from "swr";
import { AxiosRequestConfig, Method } from "axios";
import { createSearchParams } from "react-router-dom";
import axiosInstance from "../axios/axios-instance";

import { api as configs } from "../utils/config";
import { omitEmpty } from "../utils/utils";
import { pathAPI } from "../utils/pathApi";

type RequestArgs = {
  path?: string;
  params?: any;
  options?: AxiosRequestConfig;
  isAuth?: boolean;
};

export const request = async <T>({
  path = "",
  params = {},
  options = {} as AxiosRequestConfig,
  isAuth = false,
}: RequestArgs): Promise<T> => {
  let url = configs.baseUrl + path;
  const token = localStorage.getItem("token");
  const currentLanguage = localStorage.getItem("language");
  const method = String(options.method || "GET").toUpperCase();
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> | undefined),
    lang: currentLanguage || "en",
  };
  let data: any;
  if (method === "POST" || method === "PUT") {
    if (params) {
      if (params instanceof FormData) {
        data = params;
      } else {
        data = params;
        headers["Content-Type"] = "application/json";
      }
    }
  } else if (params) {
    url += `?${createSearchParams(omitEmpty(params))}`;
  }
  if (isAuth) {
    headers.Authorization = `Bearer ${token}`;
  }

  try {
    const response = await axiosInstance.request<T>({
      ...options,
      url,
      method: method as Method,
      headers,
      data,
    });
    return response.data;
  } catch (error: any) {
    throw error?.response?.data || error;
  }
};

export function useAPI<T>(
  path?: string | null | false,
  isAuth = false,
  params?: object,
  options?: AxiosRequestConfig,
  swrOptions?: SWRConfiguration
) {
  const { data, isLoading, error, mutate } = useSWR<
    T,
    Error & { code?: number }
  >(
    path ? [{ path, params, options, isAuth }, "useAPI"] : null,
    ([params]: [Parameters<typeof request>[0]]) => request(params) as any,
    swrOptions || {
      revalidateOnFocus: false,
      revalidateIfStale: false,
      shouldRetryOnError: false,
    }
  );
  return { data, error, loading: isLoading, mutate };
}

const api = {
  GET<T = any>(
    path = "",
    params = {},
    options = {} as AxiosRequestConfig,
    isAuth = false
  ) {
    return request<T>({
      path,
      params,
      options: { ...options, method: "GET" },
      isAuth,
    });
  },
  POST<T = any>(
    path = "",
    params = {},
    options = {} as AxiosRequestConfig,
    isAuth = false
  ) {
    return request<T>({
      path,
      params,
      options: { ...options, method: "POST" },
      isAuth,
    });
  },
  PUT<T = any>(
    path = "",
    params = {},
    options = {} as AxiosRequestConfig,
    isAuth = false
  ) {
    return request<T>({
      path,
      params,
      options: { ...options, method: "PUT" },
      isAuth,
    });
  },
  DELETE<T = any>(
    path = "",
    params = {},
    options = {} as AxiosRequestConfig,
    isAuth = false
  ) {
    return request<T>({
      path,
      params,
      options: { ...options, method: "DELETE" },
      isAuth,
    });
  },
  // Handle child category creation with translations
  async createChildCategory(parentId: string, formData: FormData, translations: any[] = []) {
    try {
      // Step 1: Create the child category first
      const response = await this.POST(`${pathAPI.categoryDetail}/${parentId}`, formData, {}, true);
      
      // If we have translations and the category was created successfully
      if (translations.length > 0 && response.data && response.data.id) {
        const newChildId = response.data.id;
        
        // Step 2: Add translations for the newly created child category
        const translationsPayload = translations.map(item => ({
          translation_id: null,
          translation_name: item.translation_name || "",
          language_id: item.language_id,
          language_code: item.language_code
        }));
        
        // Use the generic /{categoryId}/translations endpoint
        await this.PUT(`${pathAPI.categoryDetail}/${newChildId}/translations`, translationsPayload, {}, true);
      }
      
      return response;
    } catch (error) {
      throw error;
    }
  },
};

export default api;
